/*
  KENZEN: the add-to-cart button takes the colour of the selected colour swatch.

  Port of the live Stiletto 3.2.1 customisation (three copies of the same inline
  script in layout/theme.liquid, snippets/product-form.liquid and
  sections/main-product--quick.liquid) rebuilt once for Stiletto 6.3.0.

  How it works
  - Scope = a product section ([data-section-type="product"] incl. the quick
    view root, which also carries data-is-quick-view="true", and
    [data-section-type="featured-product"]).
  - The selected swatch is `.product__color-swatch.selected`; its inline
    background-color comes from the `custom_swatch_colors` theme setting
    (snippets/product-swatch.liquid).
  - 6.0 renamed the button variables: the primary button paints its ::before
    with --color-solid-button-background and its text with
    --color-solid-button-text (assets/theme.css `.btn.btn--primary`). The
    hover colour (--color-solid-button-background-hover, shown by ::after when
    ::before slides away) is NOT overridden, exactly like live.
  - Variables are set on the scope (like live) AND on every add-to-cart button
    inside it, because the sticky bar and the quick-view modal carry their own
    inline colour-scheme variables that would otherwise shadow the scope value.
  - Repaint on swatch click (the theme toggles `.selected` synchronously in the
    click handler) and on DOM changes inside the scope (quick view / section
    refresh), plus a body observer that wires quick-view roots when inserted.
*/
(function () {
  'use strict';

  var SCOPE_SELECTOR = '[data-section-type="product"], [data-section-type="featured-product"]';
  var SWATCH_SELECTOR = '.product__color-swatch.selected';
  var BUTTON_SELECTOR = 'button[name="add"][data-add-to-cart]';
  var VAR_BG = '--color-solid-button-background';
  var VAR_TEXT = '--color-solid-button-text';
  var TRANSITION = 'background-color .2s ease, color .2s ease, border-color .2s ease, filter .15s ease';

  function parseRgb(c) {
    if (!c) return null;
    c = String(c).trim();
    if (c.charAt(0) === '#') {
      var v = c.slice(1);
      if (v.length === 3 || v.length === 4) {
        v = v.split('').map(function (x) { return x + x; }).join('');
      }
      var n = parseInt(v.slice(0, 6), 16);
      if (isNaN(n)) return null;
      return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255, a: 1 };
    }
    // rgb(r, g, b) / rgba(r, g, b, a) / rgb(r g b / a)
    var m = c.match(/rgba?\(\s*(\d+)[\s,]+(\d+)[\s,]+(\d+)(?:[\s,\/]+([\d.]+%?))?/i);
    if (!m) return null;
    var a = 1;
    if (m[4] !== undefined) {
      a = m[4].slice(-1) === '%' ? parseFloat(m[4]) / 100 : parseFloat(m[4]);
    }
    return { r: +m[1], g: +m[2], b: +m[3], a: a };
  }

  function contrast(rgb) {
    return (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) > 186 ? '#000' : '#fff';
  }

  function setVars(el, bg, fg) {
    el.style.setProperty(VAR_BG, bg, 'important');
    el.style.setProperty(VAR_TEXT, fg, 'important');
  }

  function clearVars(el) {
    el.style.removeProperty(VAR_BG);
    el.style.removeProperty(VAR_TEXT);
  }

  function paintButton(btn, bg, fg) {
    setVars(btn, bg, fg);
    btn.style.setProperty('background-image', 'none', 'important');
    btn.style.setProperty('background-color', bg, 'important');
    btn.style.setProperty('border-color', bg, 'important');
    btn.style.setProperty('color', fg, 'important');
    btn.style.transition = TRANSITION;
  }

  function clearButton(btn) {
    clearVars(btn);
    btn.style.removeProperty('background-image');
    btn.style.removeProperty('background-color');
    btn.style.removeProperty('border-color');
    btn.style.removeProperty('color');
    btn.style.removeProperty('transition');
  }

  function paint(scope) {
    var swatch = scope.querySelector(SWATCH_SELECTOR);
    if (!swatch) return;

    var bg = swatch.style.backgroundColor || getComputedStyle(swatch).backgroundColor;
    var rgb = parseRgb(bg);
    var buttons = scope.querySelectorAll(BUTTON_SELECTOR);

    // No usable colour (image swatch / unknown colour name -> transparent):
    // keep the theme's default button colours.
    if (!rgb || rgb.a === 0) {
      if (scope.__kzSwatchBg) {
        clearVars(scope);
        Array.prototype.forEach.call(buttons, clearButton);
        scope.__kzSwatchBg = '';
      }
      return;
    }

    var fg = contrast(rgb);

    if (scope.__kzSwatchBg !== bg) {
      setVars(scope, bg, fg);
      scope.__kzSwatchBg = bg;
    }

    Array.prototype.forEach.call(buttons, function (btn) {
      if (btn.style.getPropertyValue(VAR_BG) !== bg || btn.style.getPropertyValue('color') !== fg) {
        paintButton(btn, bg, fg);
      }
    });
  }

  function schedulePaint(scope) {
    if (scope.__kzSwatchQueued) return;
    scope.__kzSwatchQueued = true;
    setTimeout(function () {
      scope.__kzSwatchQueued = false;
      if (scope.isConnected) paint(scope);
    }, 0);
  }

  function wireScope(scope) {
    if (!scope || scope.__kzSwatchWired) return;
    scope.__kzSwatchWired = true;

    paint(scope);

    scope.addEventListener('click', function (e) {
      var target = e.target instanceof Element ? e.target : null;
      if (target && target.closest('.product__color-swatch')) schedulePaint(scope);
    });

    // Repaint when the theme re-renders parts of the scope or toggles classes
    // (quick view, section refresh on variant change). `style` is deliberately
    // not observed: our own repaint writes inline styles.
    var observer = new MutationObserver(function () { schedulePaint(scope); });
    observer.observe(scope, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
    scope.__kzSwatchObserver = observer;
  }

  function unwireScope(scope) {
    if (scope.__kzSwatchObserver) {
      scope.__kzSwatchObserver.disconnect();
      scope.__kzSwatchObserver = null;
    }
    scope.__kzSwatchWired = false;
  }

  function scopesIn(node) {
    if (!(node instanceof Element)) return [];
    var list = node.matches(SCOPE_SELECTOR) ? [node] : [];
    return list.concat(Array.prototype.slice.call(node.querySelectorAll(SCOPE_SELECTOR)));
  }

  function boot() {
    Array.prototype.forEach.call(document.querySelectorAll(SCOPE_SELECTOR), wireScope);

    // Theme editor: sections (re)loaded
    document.addEventListener('shopify:section:load', function (ev) {
      scopesIn(ev.target).forEach(wireScope);
    });

    // Quick view: the theme injects [data-is-quick-view] (also a product scope)
    // into the modal when it opens and empties it when it closes.
    var bodyObserver = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var m = mutations[i];
        for (var j = 0; j < m.addedNodes.length; j++) scopesIn(m.addedNodes[j]).forEach(wireScope);
        for (var k = 0; k < m.removedNodes.length; k++) scopesIn(m.removedNodes[k]).forEach(unwireScope);
      }
    });
    bodyObserver.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
